import { ActionCreator, MemoizedSelector } from '@ngrx/store';
import type { TypedAction } from '@ngrx/store/src/models';

import { AppStoreState } from '@interfaces/app-store-state';
import { BalanceConsistencyCheck } from '@models/balance-consistency-check';
import { ConsistencyCheck } from '@models/consistency-check';
import { VerboseConsistencyCheck, VerboseConsistencyCheckStatus } from '@models/verbose-consistency-check';

export const FEATURE_LIST = [
  'loans',
  'transactions',
  'balance',
  'fiscalResults',
  'immobilisations',
  'pcgEntries',
  'labels',
  'vatFramework',
  'caFramework',
  'accountingPeriods',
  'companies',
  'persons',
  'bankAccounts',
  'resultAllocations',
  'tns'
] as const;

export type Features = (typeof FEATURE_LIST)[number];

export type InferredConsistencyCheckFromFeatures<T extends Features> = T extends 'balance'
  ? BalanceConsistencyCheck
  : T extends
        | 'labels'
        | 'transactions'
        | 'caFramework'
        | 'vatFramework'
        | 'companies'
        | 'persons'
        | 'bankAccounts'
        | 'accountingPeriods'
        | 'resultAllocations'
        | 'tns'
        | 'immobilisations'
        | 'fiscalResults'
        | 'loans'
        | 'pcgEntries'
    ? VerboseConsistencyCheck
    : ConsistencyCheck;

export type UpdateActionType<T extends Features = Features> = ActionCreator<
  `[ConsistencyChecks] Update ${T}`,
  (props: { consistencyChecks: InferredConsistencyCheckFromFeatures<T>[] }) => {
    consistencyChecks: InferredConsistencyCheckFromFeatures<T>[];
  } & TypedAction<`[ConsistencyChecks] Update ${T}`>
>;
export type LoadActionType<T extends Features = Features> = ActionCreator<
  `[ConsistencyChecks] Load ${T}`,
  () => TypedAction<`[ConsistencyChecks] Load ${T}`>
>;

export type ExpireAction<T extends Features = Features> = ActionCreator<
  `[ConsistencyChecks] Expire ${T}`,
  () => TypedAction<`[ConsistencyChecks] Expire ${T}`>
>;

export interface FeatureActions<T extends Features = Features> {
  load: LoadActionType<T>;
  update: UpdateActionType<T>;
  expire: ExpireAction<T>;
}

export type FeaturesActions = {
  [key in Features]: FeatureActions<key>;
};

export interface FeatureSelectors<T extends Features = Features> {
  data: MemoizedSelector<AppStoreState, InferredConsistencyCheckFromFeatures<T>[]>;
  loading: MemoizedSelector<AppStoreState, boolean>;
  valid: MemoizedSelector<AppStoreState, boolean>;
  expired: MemoizedSelector<AppStoreState, boolean>;
  status: MemoizedSelector<AppStoreState, VerboseConsistencyCheckStatus | void>;
}

export type FeaturesSelectors = {
  [k in Features]: FeatureSelectors<k>;
};

export type FeaturesLoading = `${Features}Loading`;
export type FeaturesExpired = `${Features}Expired`;
